import {
  type AtomStore,
  AtomStoreContext,
  type BackendConnectionStatus,
  backendConnectionStore,
  type FrontendProvision,
  FrontendProvisionProvider,
  useUserStore,
} from "@atoms/atom-client";
import {
  type AtomData,
  type ContextValues,
  GenericPermission,
  makePermission,
  type UserPublicID,
  type ValidProjectSpecificPermissions,
} from "@atoms/atom-types";
import {
  type ReactNode,
  type Ref,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from "react";

/** An atom type the form of a {@link MagicFormTestProvider} writes. */
export interface MagicFormTestAtom {
  /** The entity type the atom belongs to. */
  entityType: string;
  /**
   * The fields tagged `@requireExplicitWritePermission`. The permissive
   * default grants their field-level Create and Update permissions.
   */
  requireExplicitWritePermissionFields?: string[];
}

export interface MagicFormTestProviderProps {
  /**
   * The atom types of the forms under test, by atom type: the entity type, or
   * the atom's info.
   */
  atoms: Readonly<Record<string, string | MagicFormTestAtom>>;
  /**
   * The stored value of each atom an edit-mode form reads, by atom type.
   * `null` is an atom that does not exist (the form throws
   * `AtomNotFoundError`). An atom type without an entry is still loading: its
   * form stays disabled. The value is copied; change it with
   * {@link MagicFormTestHandle.setValue}. A save does not change it: echo the
   * save with `setValue`, or the form's reload after 10 s brings the old value
   * back.
   *
   * The fake store does not model everything: it serves the same value
   * whatever the context, so a missing `extraContext` key does not throw
   * `No valid context for atom X` as in a real form; a subscription never
   * fails; and `refreshAtom` resolves in the same tick, so a test cannot see
   * the form while it reloads.
   */
  values?: Readonly<Record<string, AtomData | null>>;
  /**
   * The permissions of the acting user. Defaults to every permission a spawn
   * or edit form asks for: Read, Create, Update and Delete on each entity
   * type, Read and Update on each atom type, and Create and Update on each
   * tagged field. Pass a narrower list (or `[]`) to test a read-only form.
   */
  permissions?: readonly ValidProjectSpecificPermissions[];
  /**
   * The id of the acting user. Defaults to `"test-user"`. `useMyUserId`
   * prefers `localStorage["impersonation-user-id"]` over this id, so a test
   * that sets an impersonation sees that user.
   */
  userId?: string;
  /**
   * Whether the backend connection is up. Defaults to `true`; with `false`
   * the save buttons are disabled as offline.
   */
  connected?: boolean;
  /** The {@link MagicFormTestHandle} of this provider. */
  ref?: Ref<MagicFormTestHandle>;
  children: ReactNode;
}

/** Changes the stored values of a {@link MagicFormTestProvider}. */
export interface MagicFormTestHandle {
  /**
   * Stores a new value of the atom, as another user's write or the echo of a
   * save does, and sends it to every form that reads it. `null` deletes the
   * atom. Call it inside `act`.
   */
  setValue(atomType: string, value: AtomData | null): void;
  /**
   * Stores a new value of the atom without sending it, as a write whose
   * realtime update the form missed. The form keeps its value until the next
   * `refreshAtom`, which the reload after a stale save calls.
   */
  setMissedValue(atomType: string, value: AtomData | null): void;
}

const TEST_ROLE = "MagicFormTestRole";

const ENTITY_PERMISSIONS = [
  GenericPermission.Read,
  GenericPermission.Create,
  GenericPermission.Update,
  GenericPermission.Delete,
];
const ATOM_AND_FIELD_PERMISSIONS = [
  GenericPermission.Read,
  GenericPermission.Update,
];

const atomInfoOf = (atom: string | MagicFormTestAtom): MagicFormTestAtom =>
  typeof atom === "string" ? { entityType: atom } : atom;

function permissiveSet(
  atoms: Readonly<Record<string, MagicFormTestAtom>>,
): ValidProjectSpecificPermissions[] {
  return Object.entries(atoms).flatMap(([atomType, info]) => [
    ...ENTITY_PERMISSIONS.map((permission) =>
      makePermission({ entityType: info.entityType, permission }),
    ),
    ...ATOM_AND_FIELD_PERMISSIONS.map((permission) =>
      makePermission({ entityType: info.entityType, atomType, permission }),
    ),
    ...(info.requireExplicitWritePermissionFields ?? []).flatMap((field) =>
      [GenericPermission.Create, GenericPermission.Update].map((permission) =>
        makePermission({
          entityType: info.entityType,
          atomType,
          field,
          permission,
        }),
      ),
    ),
  ]);
}

type FakeStoreCallback = (value: AtomData | null | undefined) => void;

/**
 * The part of the AtomStore a magic form reads: the atom infos, a
 * transaction, and the stored value of each atom, served whatever the
 * context.
 */
function makeFakeStore(
  atoms: Readonly<Record<string, MagicFormTestAtom>>,
  initialValues: Readonly<Record<string, AtomData | null>>,
) {
  // Copied: `useAtom` freezes what the store returns.
  const values = new Map<string, AtomData | null>(
    Object.entries(initialValues).map(([atomType, value]) => [
      atomType,
      structuredClone(value),
    ]),
  );
  // Written, but not sent yet: `refreshAtom` delivers them.
  const missedValues = new Map<string, AtomData | null>();
  const listeners = new Map<string, Set<FakeStoreCallback>>();
  const listenersOf = (atomType: string) => {
    let set = listeners.get(atomType);
    if (!set) {
      set = new Set();
      listeners.set(atomType, set);
    }
    return set;
  };
  const pathForAtomWithContext = (atomType: string) => {
    if (!atoms[atomType]) {
      throw new Error(`No entity type mapping for atom ${atomType}`);
    }
    return `/${atomType}`;
  };
  const getAtom = (atomType: string) => {
    pathForAtomWithContext(atomType);
    return values.get(atomType);
  };
  const emit = (atomType: string) => {
    // A copy, as the real store: a callback may unsubscribe or subscribe.
    const callbacks = [...listenersOf(atomType)];
    for (const callback of callbacks) {
      callback(values.get(atomType));
    }
  };
  const store = {
    atomInfosExtended: atoms,
    getEntityType: (atomType: string) => atoms[atomType]?.entityType,
    getTransaction: (context: ContextValues) => ({ context }),
    pathForAtomWithContext,
    getAtom,
    subscribe: (
      atomType: string,
      _context: ContextValues,
      callback: FakeStoreCallback,
    ) => {
      pathForAtomWithContext(atomType);
      listenersOf(atomType).add(callback);
    },
    unsubscribe: async (
      atomType: string,
      _context: ContextValues,
      callback: FakeStoreCallback,
    ) => {
      listeners.get(atomType)?.delete(callback);
    },
    // As the real store: the value arrives again, `false` when nothing
    // subscribes to the atom.
    refreshAtom: async (atomType: string) => {
      pathForAtomWithContext(atomType);
      if (listenersOf(atomType).size === 0) {
        return false;
      }
      if (missedValues.has(atomType)) {
        values.set(atomType, missedValues.get(atomType) ?? null);
        missedValues.delete(atomType);
      }
      emit(atomType);
      return true;
    },
  } as unknown as AtomStore;
  const handle: MagicFormTestHandle = {
    setValue: (atomType, value) => {
      pathForAtomWithContext(atomType);
      missedValues.delete(atomType);
      values.set(atomType, structuredClone(value));
      emit(atomType);
    },
    setMissedValue: (atomType, value) => {
      pathForAtomWithContext(atomType);
      missedValues.set(atomType, structuredClone(value));
    },
  };
  return { store, handle };
}

function makeSetup({
  atoms,
  values,
  permissions,
}: Pick<MagicFormTestProviderProps, "atoms" | "values" | "permissions">) {
  const atomInfos = Object.fromEntries(
    Object.entries(atoms).map(([atomType, atom]) => [
      atomType,
      atomInfoOf(atom),
    ]),
  );
  const provision = {
    branding: { appName: "Test" },
    pageIndex: {},
    getRolesFromContext: async () => [TEST_ROLE],
    permissionsByRoles: {
      [TEST_ROLE]: [...(permissions ?? permissiveSet(atomInfos))],
    },
  } as FrontendProvision;
  return { provision, ...makeFakeStore(atomInfos, values ?? {}) };
}

type GlobalState = { userId: string; connected: boolean };

/** The mounted providers, in mount order: the last one sets the state. */
const mountedStates: GlobalState[] = [];
/** The state before the first of the mounted providers. */
let stateBeforeProviders:
  | {
      userId: UserPublicID | null;
      connection: ReturnType<typeof backendConnectionStore.getSnapshot>;
    }
  | undefined;

function setConnectionStatus(status: BackendConnectionStatus) {
  // Unchanged: no transition, so no connection generation or timestamp moves.
  if (backendConnectionStore.getSnapshot().status !== status) {
    backendConnectionStore.setStatus(status);
  }
}

function applyState({ userId, connected }: GlobalState) {
  useUserStore.setState({ userId: userId as UserPublicID });
  setConnectionStatus(connected ? "connected" : "disconnected");
}

/**
 * Sets the user id and the connection state. Unmounting gives the state back
 * to the provider mounted before, and after the last one to the state before
 * the first, in any unmount order.
 */
function mountGlobalState(state: GlobalState): () => void {
  if (mountedStates.length === 0) {
    stateBeforeProviders = {
      userId: useUserStore.getState().userId,
      connection: backendConnectionStore.getSnapshot(),
    };
  }
  mountedStates.push(state);
  applyState(state);
  return () => {
    mountedStates.splice(mountedStates.indexOf(state), 1);
    const remaining = mountedStates.at(-1);
    if (remaining) {
      applyState(remaining);
      return;
    }
    if (!stateBeforeProviders) return;
    const { userId, connection } = stateBeforeProviders;
    stateBeforeProviders = undefined;
    useUserStore.setState({ userId });
    if (connection.issue === "final") {
      backendConnectionStore.setFinalDisconnected();
    } else {
      setConnectionStatus(connection.status);
    }
  };
}

/**
 * Renders its children with what a magic form needs, in spawn mode
 * (`spawnMode`) or edit mode (a form over a stored atom), so a component test
 * runs the real loading, drafting, permission and submit code instead of
 * mocking `__useAtom`, `useAtomStore`, `useAtomRefresh`,
 * `useCombinedContext`, `usePermissionsForContext` or
 * `useMagicFormFieldPermission`:
 *
 * - a frontend provision with one role that holds the permissions (by
 *   default every permission a spawn or edit form asks for),
 * - a fake AtomStore that knows the entity type and the info of each atom
 *   and serves the stored `values`; `refreshAtom` sends the stored value
 *   again, or the one a test set with `setMissedValue`, so the reload after a
 *   stale save runs too,
 * - the acting user's id in `useUserStore`,
 * - a connected `backendConnectionStore`.
 *
 * The `ref` handle changes a stored value while the form is mounted (another
 * user's write, or the backend's echo of a save):
 * `act(() => handle.current?.setValue("Article", next))`. `setMissedValue`
 * stores a write whose realtime update the form missed: only `refreshAtom`
 * delivers it. A save does not change the stored value by itself.
 *
 * The user id and the connection state are global; the provider restores
 * them on unmount (nested or overlapping providers included: the last mounted
 * one wins). Only the connection status is restored: the timestamps and the
 * connection generation keep the transitions in between, and after
 * `connected={false}` a store that was still starting up reports
 * `issue: "reconnecting"`. Its props are read once, on mount: remount it
 * (`key`) to change them. The children render once the state is set.
 *
 * The roles resolve asynchronously, so fields and the save button start
 * disabled. Before the first change,
 * `await waitFor(() => expect(field).toBeEnabled())`, even when the field
 * already is enabled; an edit form also waits for its stored value
 * (`toHaveValue`). A change fired in the same tick as the first lookup can be
 * overwritten by the form's pending updates (seen: a cleared number snapping
 * back). Before the click, wait for the save button too:
 * `await waitFor(() => expect(save).toBeEnabled())`; a click on a disabled
 * button does nothing. Pure React; works with any test runner.
 *
 * @example
 * ```tsx
 * const MagicArticleForm = makeShadcnMagicForm<"Article", IArticle>({
 *   atom: "Article",
 * });
 *
 * it("saves the changed name", async () => {
 *   const onSubmit = vi.fn(async () => {});
 *   render(
 *     <MagicFormTestProvider
 *       atoms={{ Article: "Catalog" }}
 *       values={{ Article: { name: "Bolt", notes: "" } }}
 *     >
 *       <MagicArticleForm onSubmit={onSubmit}>
 *         <MagicArticleForm.Input field="name" aria-label="name" />
 *         <MagicSaveButton />
 *       </MagicArticleForm>
 *     </MagicFormTestProvider>,
 *   );
 *   const name = screen.getByLabelText("name");
 *   await waitFor(() => expect(name).toHaveValue("Bolt"));
 *   await waitFor(() => expect(name).toBeEnabled());
 *   fireEvent.change(name, { target: { value: "Nut" } });
 *   const save = screen.getByRole("button", { name: /speichern/i });
 *   await waitFor(() => expect(save).toBeEnabled());
 *   fireEvent.click(save);
 *   await waitFor(() =>
 *     expect(onSubmit).toHaveBeenCalledWith(
 *       expect.objectContaining({ changes: { name: "Nut" } }),
 *     ),
 *   );
 * });
 * ```
 */
export function MagicFormTestProvider({
  atoms,
  values,
  permissions,
  userId = "test-user",
  connected = true,
  ref,
  children,
}: Readonly<MagicFormTestProviderProps>) {
  const [setup] = useState(() => makeSetup({ atoms, values, permissions }));
  useImperativeHandle(ref, () => setup.handle, [setup]);
  const [mountState] = useState({ userId, connected });
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    const unmount = mountGlobalState(mountState);
    setReady(true);
    return unmount;
  }, [mountState]);

  return (
    <FrontendProvisionProvider provision={setup.provision}>
      <AtomStoreContext.Provider value={setup.store}>
        {ready ? children : null}
      </AtomStoreContext.Provider>
    </FrontendProvisionProvider>
  );
}
